<template>
  <svg
    class="spark"
    :width="width"
    :height="height"
    :viewBox="`0 0 ${width} ${height}`"
    preserveAspectRatio="none"
    role="img"
    :aria-label="label"
  >
    <path v-if="areaPath" class="spark-fill" :d="areaPath" />
    <path v-if="linePath" class="spark-line" :d="linePath" />
    <circle v-if="lastPoint" class="spark-dot" :cx="lastPoint[0]" :cy="lastPoint[1]" r="1.8" />
  </svg>
</template>

<script>
/**
 * The shape of a number over time, next to the number itself.
 *
 * Deliberately has no axis, no gridline, no tick and no legend: the number
 * beside it is the label, and this only has to answer "which way, and how
 * steadily". Adding any of those turns a glanceable mark into a small bad chart.
 *
 * It draws only complete periods. A part-finished day plotted next to whole ones
 * always dives at the right-hand end and reads as a collapse, which is the same
 * trap as comparing today's takings with the whole of yesterday.
 */
export default {
  name: "SparkLine",
  props: {
    values: { type: Array, required: true },
    width: { type: Number, default: 72 },
    height: { type: Number, default: 24 },
    label: { type: String, default: "" },
  },
  computed: {
    points() {
      const v = this.values.map((n) => Number(n) || 0);
      if (v.length < 2) return [];
      const min = Math.min(...v);
      const max = Math.max(...v);
      // A flat series would divide by zero and also has no shape to show, so it
      // is drawn down the middle rather than pinned to an edge.
      const span = max - min || 1;
      const pad = 3; // keeps the 2px stroke and the end dot inside the box
      const w = this.width;
      const h = this.height - pad * 2;
      return v.map((n, i) => [
        (i / (v.length - 1)) * w,
        pad + h - ((n - min) / span) * h,
      ]);
    },
    linePath() {
      if (!this.points.length) return "";
      return this.points.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
    },
    areaPath() {
      if (!this.points.length) return "";
      const p = this.points;
      return (
        this.linePath +
        ` L${p[p.length - 1][0].toFixed(1)} ${this.height} L${p[0][0].toFixed(1)} ${this.height} Z`
      );
    },
    lastPoint() {
      return this.points.length ? this.points[this.points.length - 1] : null;
    },
  },
};
</script>

<style scoped>
.spark {
  display: block;
  overflow: visible;
}
/* currentColor throughout, so a caller decides the meaning by setting `color`
   on the wrapper: muted by default, semantic where the direction matters. */
.spark-line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}
.spark-fill {
  fill: currentColor;
  opacity: 0.12;
  stroke: none;
}
.spark-dot {
  fill: currentColor;
}
</style>
